<template>
  <div>
    <!-- オーバーレイ -->
    <CommonOverlay
      @handleOverlayClick="handleOverlayClick"
      class="menu-overlay"
      :class="{ active: isVisible }"
    >
      <!-- カート本体 -->
      <div class="cart-drawer" :class="{ active: isVisible }">
        <div class="drawer-content">
          <div
            class="close-button"
            @click="$store.dispatch('market/toggleCart')"
          >
            ×
          </div>
          <div class="title">
            <h2>{{ t('カート') }}</h2>
            <img src="/img/menu/cart.png" />
          </div>
          <div class="cart-items">
            <div class="cart-header">
              <div class="item-name">{{ t('商品名') }}</div>
              <div class="item-price">{{ t('金額') }}</div>
              <div class="item-remove"></div>
            </div>

            <div
              class="cart-row"
              v-for="(cartItem, index) in cartList.list"
              :key="index"
            >
              <div class="image-area">
                <img :src="cartItem.card_image" class="card-img" />
                <div class="info-box">
                  <div class="name">{{ cartItem.card_name }}</div>
                  <div class="rarity">{{ cartItem.card_rarity }}</div>
                  <div class="model-number">
                    {{ cartItem.card_model_number }}
                  </div>
                </div>
              </div>
              <div class="item-price">
                {{ cartItem.price | comma }}{{ t('円') }}
              </div>
              <div class="item-remove">
                <span
                  @click="$emit('deleteItem', cartItem)"
                  class="remove-btn"
                  >{{ t('削除') }}</span
                >
              </div>
            </div>

            <div class="cart-summary">
              <div class="summary-row">
                <span class="price-title">{{ t('小計') }}</span>
                <span>{{ subtotal | comma }}{{ t('円') }}</span>
              </div>
              <div class="summary-row">
                <span class="price-title">{{ t('送料') }}</span>
                <span>{{ shipping | comma }}{{ t('円') }}</span>
              </div>
              <div class="summary-row total">
                <span class="price-title">{{ t('合計') }}</span>
                <span>{{ total | comma }}{{ t('円') }}</span>
              </div>
            </div>
          </div>
          <ErrorMessage
            v-if="errorMessage"
            class="error-message"
            :message="errorMessage"
          />
          <div class="button-area">
            <BuyButton
              class="purchase-btn"
              :class="{ none: !subtotal }"
              :text="t('購入手続きへ')"
              :isArrow="false"
              @handleSquareBtnClick="checkCart"
            />
          </div>
        </div>
      </div>
    </CommonOverlay>
    <AddressPopup
      v-if="isShowAddress"
      @handleSuccess="successAddress"
      @handleClose="isShowAddress = false"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import { separateCharacterNumber } from '@/common/util/format';
import t from '@/plugins/i18n';
import CommonOverlay from '@/components/atoms/CommonOverlay.vue';
import BuyButton from '@/components/atoms/BuyButton.vue';
import ErrorMessage from '@/components/atoms/ErrorMessage.vue';
import AddressPopup from '@/components/organisms/AddressPopup.vue';

export default {
  name: 'Cart',
  mixins: [t],

  components: {
    CommonOverlay,
    BuyButton,
    ErrorMessage,
    AddressPopup,
  },

  filters: {
    comma(value) {
      return separateCharacterNumber(value);
    },
  },

  data() {
    return {
      errorMessage: null,
      isShowAddress: false,
    };
  },

  created() {
    // ユーザー詳細情報を取得
    this.$store.dispatch('user/setCurrentUserDetail');
  },

  watch: {
    resultMarketCheckCart(newResult) {
      if (newResult.removed_items && newResult.removed_items.length > 0) {
        const reasons = newResult.removed_items
          .map((item) => `${item.card_name}: ${item.reason}`)
          .join('\n');
        this.errorMessage = reasons;
        this.$store.dispatch('market/historyList');
        return;
      }
      this.errorMessage = null;
      this.$router.push({ path: '/market-confirm-buy' });
    },
  },

  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    cartList: {
      type: [Object, Array],
      default: () => [],
    },
    userDetail: {
      type: Object,
      default: () => ({}),
    },
  },

  computed: {
    ...mapGetters({
      resultMarketCheckCart: 'market/resultMarketCheckCart',
      currentUserDetail: 'user/currentUserDetail',
    }),
    subtotal() {
      if (!this.cartList) return 0;
      return this.cartList.list.reduce((sum, item) => {
        const price = parseInt(item.price, 10) || 0;
        return sum + price;
      }, 0);
    },
    shipping() {
      return this.cartList.shipping_fee || 0;
    },
    total() {
      return this.subtotal + this.shipping;
    },
  },
  methods: {
    handleOverlayClick() {
      this.$emit('handleOverlayClick');
    },
    checkCart() {
      // 住所が登録されているかチェック
      this.checkAddress();
    },
    checkAddress() {
      // 住所が登録されているかチェック
      const hasAddress = this.currentUserDetail
        && this.currentUserDetail.address
        && this.currentUserDetail.zipcode
        && this.currentUserDetail.tel;
      if (hasAddress) {
        // 住所が登録済みの場合は直接カートチェック
        this.$store.dispatch('market/check_cart', {
          postParams: {},
        });
      } else {
        // 住所が未登録の場合は住所登録画面を表示
        this.isShowAddress = true;
      }
    },
    successAddress() {
      this.isShowAddress = false;
      // 住所登録完了後、カートチェックを実行
      this.$store.dispatch('market/check_cart', {
        postParams: {},
      });
    },
  },
};
</script>

<style scoped lang="scss">
.menu-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.5);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;
  z-index: 1000;
  &.active {
    opacity: 1;
    visibility: visible;
  }
}

.menu-overlay.active {
  display: block;
}

.cart-drawer {
  position: fixed;
  top: 0;
  right: 0;
  height: 100%;
  width: 350px;
  background: white;
  transform: translateX(105%);
  transition: transform 0.3s ease;
  z-index: 1001;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.5);
  color: $gray2;
  &.active {
    transform: translateX(0);
  }
  .drawer-content {
    padding: 10px;
    height: 100dvh;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    position: relative;
    .title {
      display: flex;
      align-items: center;
      justify-content: center;
      img {
        width: 40px;
        height: 40px;
      }
    }
    .cart-items {
      flex: 1;
      overflow-y: auto;
      padding: 20px 2px;
      .cart-header,
      .cart-row {
        display: flex;
        align-items: center;
        border-bottom: 1px solid #ccc;
        font-size: 14px;
        font-weight: bold;
        .item-name {
          flex: 1.3;
          text-align: center;
        }
        .item-price {
          flex: 2;
          text-align: center;
        }
        .item-remove {
          flex: 1;
          text-align: center;
        }
      }
      .cart-row {
        padding: 15px 0;
        font-weight: normal;
        .image-area {
          width: 90px;
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          img {
            width: 60px;
            height: auto;
          }
          .info-box {
            display: flex;
            z-index: 1;
            flex-direction: column;
            align-items: center;
            border-radius: 4px;
            text-align: center;
            padding: 3px 0;
            background-color: white;
            width: 100%;
            box-shadow: 0 0 4px rgba(0, 0, 0, 0.7);
            margin-top: -15px;
            font-weight: bold;
            font-size: 10px;
            .name {
              display: -webkit-box;
              -webkit-box-orient: vertical;
              -webkit-line-clamp: 2;
              overflow: hidden;
              text-overflow: ellipsis;
              max-height: calc(1.3em * 2);
              line-height: 1.1;
            }
            .rarity.model-number {
              display: -webkit-box;
              -webkit-box-orient: vertical;
              -webkit-line-clamp: 1;
              overflow: hidden;
              text-overflow: ellipsis;
              max-height: calc(1.3em * 1);
              width: fit-content;
              min-width: 70%;
            }
          }
        }
        .item-price {
          font-weight: bold;
          color: black;
        }
        .item-remove {
          .remove-btn {
            color: red;
            cursor: pointer;
            font-size: 13px;
          }
        }
      }
      .cart-summary {
        margin-top: 16px;
        font-weight: bold;
        font-size: 14px;
        .summary-row {
          display: flex;
          justify-content: flex-end;
          padding: 4px 0;
          color: black;
          .price-title {
            color: $gray2;
            margin-right: 8px;
          }
          &.total {
            // border-top: 2px solid #000;
            margin-top: 6px;
            padding-top: 6px;
            font-size: 16px;
          }
        }
      }
    }
    .button-area {
      display: flex;
      justify-content: space-around;
      margin-top: 20px;
      button {
        width: 100%;
        border: none;
        cursor: pointer;
        border-radius: 0;
        font-size: 16px;
        font-weight: bold;
        box-shadow: 0 0 5px rgba(0, 0, 0, 0.4);
        @media screen and (max-width: $tabletWidth) {
          padding: 10px 40px;
        }
        &.none {
          background: #ccc;
          color: #777;
          pointer-events: none;
          cursor: not-allowed;
        }
      }
    }
  }
  .close-button {
    position: absolute;
    top: 10px;
    right: 10px;
    font-size: 28px;
    cursor: pointer;
  }
}
</style>
